import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useReviewDraft } from '../contexts/ReviewDraftContext';
import { API_URL } from '../utils/config';
import './EngineerQueryBar.css';

/**
 * Sticky bottom bar for engineer to request AI revisions (human-in-the-loop).
 * On /analysis/review: sends current draft + message to revise-draft API and adds new version.
 */
const EngineerQueryBar: React.FC = () => {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  const reviewDraft = useReviewDraft();

  const isOnReviewPage = location.pathname === '/analysis/review';
  const canRevise = isOnReviewPage && reviewDraft && reviewDraft.versions.length > 0;
  const currentDraft = canRevise && reviewDraft
    ? reviewDraft.versions[reviewDraft.activeVersionIndex]
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const message = value.trim();
    if (!message) return;
    if (!canRevise || !reviewDraft || !currentDraft) {
      setError('Open a report review to request changes.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const original_context = reviewDraft.originalContext
        ? {
            research_context: reviewDraft.originalContext.research_context ?? '',
            transcript: reviewDraft.originalContext.transcript ?? '',
            engineer_notes: reviewDraft.originalContext.engineer_notes ?? '',
            wind_report: reviewDraft.originalContext.wind_report ?? undefined,
          }
        : {};
      const response = await axios.post(
        `${API_URL.replace(/\/$/, '')}/api/reports/revise-draft`,
        { draft_report: currentDraft, user_message: message, original_context },
        { withCredentials: true, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      if (response.data?.success && response.data?.revised_report) {
        reviewDraft.addVersion(response.data.revised_report);
        setValue('');
      } else {
        setError(response.data?.error || 'Revise failed');
      }
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.error
        ? err.response.data.error
        : err instanceof Error ? err.message : 'Request failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="engineer-query-bar">
      <form className="engineer-query-bar-form" onSubmit={handleSubmit}>
        <label htmlFor="engineer-query-input" className="engineer-query-bar-label">
          Ask for changes
        </label>
        {!canRevise && (
          <p className="engineer-query-bar-hint">Open a report review to request changes.</p>
        )}
        {error && <p className="engineer-query-bar-error">{error}</p>}
        <div className="engineer-query-bar-row">
          <textarea
            id="engineer-query-input"
            className="engineer-query-bar-input"
            placeholder="e.g. Make the conclusions more concise, add a note about the roof condition…"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(''); }}
            rows={1}
            aria-label="Engineer request for AI"
            disabled={!canRevise || loading}
          />
          <button
            type="submit"
            className="engineer-query-bar-submit"
            disabled={!value.trim() || !canRevise || loading}
            aria-label="Send request"
          >
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} aria-hidden />
          </button>
        </div>
      </form>
    </div>
  );
};

export default EngineerQueryBar;
